import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, BellOff, Check } from "lucide-react";
import { PageHeader } from "@/business/components/PageHeader";
import { ComingSoonBadge } from "@/business/components/ComingSoonBadge";

const SAMPLE = [
  { id: "n1", type: "system", title: "Welcome to BuskerBeat for Business 🎉", body: "Complete your venue profile to start being discovered.", time: "Just now", unread: true },
  { id: "n2", type: "system", title: "Profile tip", body: "Add a cover photo — venues with photos get 3x more profile views.", time: "2 hours ago", unread: true },
  { id: "n3", type: "gig", title: "Heads-up: gig posting is live", body: "Post your first gig from the Hire a Busker tab.", time: "Yesterday", unread: false },
];

export default function NotificationsPage() {
  const [items, setItems] = useState(SAMPLE);
  const markAllRead = () => setItems((s) => s.map((n) => ({ ...n, unread: false })));
  const markOne = (id) => setItems((s) => s.map((n) => (n.id === id ? { ...n, unread: false } : n)));
  const unread = items.filter((n) => n.unread).length;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        description="Updates about your venue, gigs, and bookings."
        action={(<Button variant="outline" onClick={markAllRead} disabled={unread === 0} data-testid="notifications-mark-all-read-button"><Check className="mr-2 h-4 w-4" />Mark all as read</Button>)}
      />
      <Card className="border-[#E5E7EB] shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
          <div className="flex items-center gap-2"><h2 className="text-base font-semibold">Recent</h2>{unread > 0 && <Badge className="bg-[#FFF1E8] text-[#FF6A00] border border-[#FFD7BF] hover:bg-[#FFF1E8]" data-testid="notifications-unread-badge">{unread} new</Badge>}</div>
          <div className="flex items-center gap-2"><ComingSoonBadge label="Real-time soon" /></div>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="py-12 text-center" data-testid="notifications-empty-state">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-[#F3F4F6] text-[#6B7280] flex items-center justify-center"><BellOff className="h-6 w-6" /></div>
              <p className="mt-3 text-sm font-medium">No notifications</p>
              <p className="text-xs text-[#6B7280]">You’re all caught up.</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[520px]">
              <ul data-testid="notifications-list">
                {items.map((n) => (
                  <li key={n.id} onClick={() => markOne(n.id)} className={`px-4 sm:px-5 py-4 border-b border-[#E5E7EB] last:border-0 cursor-pointer transition-colors duration-150 ${n.unread ? "bg-[#FFF8F1] hover:bg-[#FFF1E8]" : "hover:bg-[#F9FAFB]"}`} data-testid={`notification-item-${n.id}`}>
                    <div className="flex items-start gap-3">
                      <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${n.unread ? "bg-[#FF6A00]" : "bg-transparent"}`} aria-hidden="true" />
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-[#E5E7EB] text-[#FF6A00] flex-shrink-0"><Bell className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-semibold truncate">{n.title}</span>
                          <span className="text-[11px] text-[#6B7280] flex-shrink-0">{n.time}</span>
                        </div>
                        <p className="text-sm text-[#374151] mt-0.5">{n.body}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
